<script setup lang="ts">
// "Lot of the day" beside the lots ending next — the auction counterpart of
// a marketplace's deal-of-the-day panel. The lot badge is the one place the
// page uses the sun highlight at full strength.
import { RouterLink } from 'vue-router'
import { Sun, ArrowRight } from 'lucide-vue-next'
import AuctionCard from '@/components/auction/AuctionCard.vue'
import { useI18n } from '@/composables/useI18n'
import type { Auction } from '@/types'

defineProps<{
  lot: Auction | null
  ending: Auction[]
}>()

const { t } = useI18n()
</script>

<template>
  <section
    v-if="lot"
    aria-labelledby="deal-title"
    class="grid items-start gap-5 rounded-[1.5rem] border border-border bg-surface p-4 sm:rounded-[2rem] sm:p-6"
    :class="ending.length ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,3fr)]' : ''"
  >
    <div class="min-w-0 space-y-3" :class="ending.length ? '' : 'max-w-sm'">
      <div class="flex items-center gap-2">
        <span class="inline-flex items-center gap-1.5 rounded-full bg-sun px-3 py-1 text-[12px] font-bold text-onSun">
          <Sun class="h-3.5 w-3.5" />{{ t('homeDiscovery.dealBadge') }}
        </span>
      </div>
      <h2 id="deal-title" class="sr-only">{{ t('homeDiscovery.dealBadge') }}</h2>
      <p class="text-[13px] text-text-secondary">{{ t('homeDiscovery.dealSubtitle') }}</p>
      <AuctionCard :auction="lot" />
    </div>

    <div v-if="ending.length" class="min-w-0 space-y-3 lg:border-l lg:border-border lg:pl-5">
      <div class="flex items-center justify-between gap-3">
        <div class="min-w-0">
          <h2 class="font-display text-lg font-bold leading-tight tracking-tight text-text-primary sm:text-xl">{{ t('home.endingSoon') }}</h2>
          <p class="mt-0.5 hidden text-[13px] text-text-secondary sm:block">{{ t('landing.endingSoonSubtitle') }}</p>
        </div>
        <RouterLink
          to="/auctions?sort=ending_soon"
          :aria-label="`${t('common.viewAll')}: ${t('home.endingSoon')}`"
          class="-my-2 -mr-2 inline-flex min-h-[40px] shrink-0 items-center gap-1 whitespace-nowrap rounded-xl px-2.5 py-2 text-sm font-semibold text-text-primary transition hover:bg-accent hover:text-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {{ t('common.viewAll') }}<ArrowRight class="h-4 w-4" />
        </RouterLink>
      </div>
      <div class="rail lg:mx-0 lg:grid lg:grid-cols-3 lg:gap-4 lg:overflow-visible lg:px-0">
        <AuctionCard
          v-for="auction in ending"
          :key="auction.id"
          :auction="auction"
          class="w-[236px] sm:w-[250px] lg:w-auto"
        />
      </div>
    </div>
  </section>
</template>
